import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ViewColActions } from '../../../application-controller/store/actions';
import { ViewColSelectors } from '../../../application-controller/store/selectors';
import { AppStates } from '../../../application-controller/store/states';
import { ViewColumnState } from '../../../application-controller/store/states/view-col.states';
import { PermissionsViewCacheActions } from '../view-cache-controller/store/actions';
import { PermissionsViewCacheSelectors } from '../view-cache-controller/store/selectors';
import { PermissionsViewCacheStates } from '../view-cache-controller/store/states';

@Injectable()
export class PermissionFacade {

  viewColState: ViewColumnState;

  clientSidePermissionCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectClientSidePermissionCache);
  fieldConfigurationCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectFieldConfiguration);
  userPermissionCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectUserPermissionCache);
  teamPermissionCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectTeamPermissionCache);
  rolePermissionCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectRolePermissionCache);
  permissionSetCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectPermissionSetCache);
  permissionWizardCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectPermissionWizardCache);
  rolePricingSchemeLinkCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectRolePricingSchemeLink);
  firstCol$ = this.appStore.select(ViewColSelectors.selectFirstColComp);
  secondCol$ = this.appStore.select(ViewColSelectors.selectSecondColComp);
  breadCrumbs$ = this.appStore.select(ViewColSelectors.selectBreadCrumbs);
  leftDrawer$ = this.appStore.select(ViewColSelectors.selectLeftDrawer);
  rightDrawer$ = this.appStore.select(ViewColSelectors.selectRightDrawer);
  toggleColumn$ = this.appStore.select(ViewColSelectors.selectSingleColumn);
  prevIndex$ = this.appStore.select(ViewColSelectors.selectPrevIndex);
  auditTrailCache$ = this.viewCacheStore.select(PermissionsViewCacheSelectors.selectAuditTrailCache);
  prevLocalState$ = () => {
    ViewColSelectors.selectPrevLocalState.release();
    return this.appStore.select(ViewColSelectors.selectPrevLocalState);
  };

  constructor(
    private readonly appStore: Store<AppStates>,
    private readonly viewCacheStore: Store<PermissionsViewCacheStates>,
    private router: Router
  ) {
    this.appStore.select(ViewColSelectors.selectViewColState).subscribe( resolve => this.viewColState = resolve);
  }

  setViewColState(state: ViewColumnState) {
    this.appStore.dispatch(ViewColActions.setViewColState({state}));
  }

  saveClientSidePermissionState() {
    this.viewCacheStore.dispatch(PermissionsViewCacheActions.cacheClientSidePermission({cache: this.viewColState}));
  }

  saveFieldConfigurationState() {
    this.viewCacheStore.dispatch(PermissionsViewCacheActions.cacheFieldConfiguration({cache: this.viewColState}));
  }

  saveUserPermissionState() {
    this.viewCacheStore.dispatch(PermissionsViewCacheActions.cacheUserPermission({cache: this.viewColState}));
  }

  saveTeamPermissionState() {
    this.viewCacheStore.dispatch(PermissionsViewCacheActions.cacheTeamPermission({cache: this.viewColState}));
  }

  saveRolePermissionState() {
    this.viewCacheStore.dispatch(PermissionsViewCacheActions.cacheRolePermission({cache: this.viewColState}));
  }

  saveAuditTrailState() {
    this.viewCacheStore.dispatch(PermissionsViewCacheActions.appletAuditTrail({cache: this.viewColState}));
  }

  savePermissionSetState() {
    this.viewCacheStore.dispatch(PermissionsViewCacheActions.cachePermissionSet({cache: this.viewColState}));
  }

  savePermissionWizardState() {
     this.viewCacheStore.dispatch(PermissionsViewCacheActions.cachePermissionWizard({cache: this.viewColState}));
  }

  saveRolePricingSchemeLinkState() {
    this.viewCacheStore.dispatch(PermissionsViewCacheActions.cacheRolePricingSchemeLink({cache: this.viewColState}));
 }

  onNext(index: number) {
    this.appStore.dispatch(ViewColActions.viewColNext({index}));
  }

  onNextAndReset(curIndex: number, nextIndex: number) {
    this.appStore.dispatch(ViewColActions.viewColNextAndReset({curIndex, nextIndex}));
  }

  onPrev(index: number) {
    this.appStore.dispatch(ViewColActions.viewColPrev({index}));
  }

  updateInstance<T>(index: number, localState: T) {
    this.appStore.dispatch(ViewColActions.viewColUpdateInstance({index, localState}));
  }

  goToIndex(index: number) {
    this.appStore.dispatch(ViewColActions.goToIndex({index}));
  }

  goBackIndex(index: number) {
    this.appStore.dispatch(ViewColActions.viewColRvIndex({index}));
  }

  goForwardIndex(index: number) {
    this.appStore.dispatch(ViewColActions.viewColFwIndex({index}));
  }

  resetIndex(index: number) {
    this.appStore.dispatch(ViewColActions.resetIndex({index}));
  }

  toggleColumn(toggle: boolean) {
    this.appStore.dispatch(ViewColActions.toggleColumn({toggle}));
  }

  selectLocalState(index: number) {
    return this.appStore.select(ViewColSelectors.selectLocalState, index);
  }

  gotoFourOhFour() {
    this.router.navigate(['404']);
  }

}
